import { db } from '../db';
import { base64ToBlob, blobToBase64, photoForStorage } from '../lib/image';
import { createId } from '../lib/id';
import { MAX_ITEM_ALIASES, MAX_ITEM_TEXT_LENGTH } from '../lib/item-values';
import type { ItemRecord, ItemStatus, OutboxEvent, SerializableItem } from '../types';

const BACKUP_VERSION = 1;
const MAX_BACKUP_ITEMS = 5_000;
const MAX_PHOTO_BASE64_LENGTH = 8_000_000;
const MAX_BACKUP_BYTES = 100_000_000;
const VALID_STATUSES = new Set<ItemStatus>(['needs_name', 'needs_location', 'needs_synonyms', 'ready']);

type BackupItem = SerializableItem & { photoBase64: string };
type HouseholdBackup = {
  format: 'household-items-backup';
  version: number;
  exportedAt: string;
  items: BackupItem[];
};

export async function downloadBackup() {
  const items = await db.items.toArray();
  const backupItems: BackupItem[] = [];
  // Encode sequentially so several full-resolution ArrayBuffers are not live at once on mobile.
  for (const { photo, ...item } of items) {
    backupItems.push({ ...item, photoBase64: await blobToBase64(photo) });
  }
  const backup: HouseholdBackup = {
    format: 'household-items-backup',
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    items: backupItems
  };

  const date = backup.exportedAt.slice(0, 10);
  const url = URL.createObjectURL(new Blob([JSON.stringify(backup)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `household-items-${date}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return items.length;
}

export async function restoreBackup(file: File) {
  if (file.size > MAX_BACKUP_BYTES) throw new Error('This backup is too large to restore safely in a browser.');
  const backup = parseBackup(await file.text());
  const currentItems = new Map((await db.items.toArray()).map((item) => [item.id, item]));
  const restored: ItemRecord[] = [];

  for (const item of backup.items) {
    const photoBase64 = item.photoBase64;
    item.photoBase64 = '';
    const current = currentItems.get(item.id);
    if (current && Date.parse(current.updatedAt) >= Date.parse(item.updatedAt)) continue;
    const { photoBase64: _releasedPhoto, ...metadata } = item;
    restored.push({
      ...metadata,
      photo: base64ToBlob(photoBase64, item.photoMimeType),
      pendingSync: Boolean(item.name.trim())
    });
  }

  const storedItems = await Promise.all(restored.map(async (item) => ({ ...item, photo: await photoForStorage(item.photo) })));
  await db.transaction('rw', db.items, db.outbox, async () => {
    await db.items.bulkPut(storedItems);
    const events: OutboxEvent[] = restored.flatMap((item) => item.name.trim() ? [{
        itemId: item.id,
        id: createId('event'),
        type: 'item.upsert',
        payload: serializableItem(item),
        createdAt: item.updatedAt
      } satisfies OutboxEvent] : []);
    await db.outbox.bulkPut(events);
  });

  return restored.length;
}

export function parseBackup(text: string): HouseholdBackup {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error('This backup file is not valid JSON.');
  }

  if (!isRecord(value) || value.format !== 'household-items-backup' || value.version !== BACKUP_VERSION || !Array.isArray(value.items)) {
    throw new Error('This is not a supported Household Items backup.');
  }
  if (value.items.length > MAX_BACKUP_ITEMS) throw new Error('This backup contains too many items for one household.');

  return {
    format: 'household-items-backup',
    version: BACKUP_VERSION,
    exportedAt: requiredString(value.exportedAt, 'export date'),
    items: value.items.map(parseItem)
  };
}

function parseItem(value: unknown): BackupItem {
  if (!isRecord(value)) throw new Error('A backup item is malformed.');
  const status = requiredString(value.status, 'item status') as ItemStatus;
  if (!VALID_STATUSES.has(status)) throw new Error('A backup item has an invalid status.');

  const photoBase64 = requiredString(value.photoBase64, 'item photo');
  if (photoBase64.length > MAX_PHOTO_BASE64_LENGTH || !/^[A-Za-z0-9+/]*={0,2}$/.test(photoBase64)) {
    throw new Error('A backup item has an invalid photo.');
  }

  const item: BackupItem = {
    id: requiredString(value.id, 'item ID'),
    name: optionalString(value.name),
    locationNote: optionalString(value.locationNote),
    locationGroup: optionalString(value.locationGroup),
    photoMimeType: optionalString(value.photoMimeType) || 'image/jpeg',
    photoBase64,
    synonyms: stringArray(value.synonyms),
    aiSuggestions: stringArray(value.aiSuggestions),
    status,
    createdAt: requiredDate(value.createdAt, 'created date'),
    updatedAt: requiredDate(value.updatedAt, 'updated date'),
    pendingSync: true
  };
  return item;
}

function serializableItem(item: ItemRecord): SerializableItem {
  const { photo: _photo, ...serializable } = item;
  return serializable;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requiredString(value: unknown, label: string) {
  if (typeof value !== 'string' || !value.trim()) throw new Error(`The backup is missing an ${label}.`);
  return value;
}

function optionalString(value: unknown) {
  return typeof value === 'string' ? value.slice(0, MAX_ITEM_TEXT_LENGTH) : '';
}

function requiredDate(value: unknown, label: string) {
  const date = requiredString(value, label);
  if (!Number.isFinite(Date.parse(date))) throw new Error(`The backup has an invalid ${label}.`);
  return date;
}

function stringArray(value: unknown) {
  return Array.isArray(value)
    ? value.flatMap((entry) => typeof entry === 'string' && entry.trim() ? [entry.trim().slice(0, MAX_ITEM_TEXT_LENGTH)] : []).slice(0, MAX_ITEM_ALIASES)
    : [];
}
